import type {
  DashboardDetailKind,
  DashboardPeriodKind,
  DashboardRankingKind,
  DashboardRankingSort,
  DashboardRole,
} from "./types";
import {
  serializeDashboardPeriod,
  type DashboardHistory,
  type DashboardPeriodState,
} from "./period";

interface DashboardActorAuthority {
  actorId: string;
}

type DashboardUnscopedAuthority = DashboardActorAuthority & {
  role: "super" | "admin_helper" | "checker" | "overviewer";
  clientId?: never;
  supplierId?: never;
  storeScope?: never;
};

type DashboardClientAuthority = DashboardActorAuthority & {
  role: "client";
  clientId: string;
  supplierId?: never;
  storeScope?: never;
};

export interface DashboardStoreAuthority {
  scopeType: "all" | "selected";
  storeIds: readonly string[];
}

type DashboardClientStoreAuthority = DashboardActorAuthority & {
  role: "client_store";
  clientId: string;
  supplierId?: never;
  storeScope: DashboardStoreAuthority;
  selectedStoreId?: string;
};

export type DashboardSupplierAuthority = DashboardActorAuthority & {
  role: "supplier";
  clientId?: never;
  supplierId: string;
  storeScope?: never;
};

export type DashboardAuthorityKeyInput =
  | DashboardUnscopedAuthority
  | DashboardClientAuthority
  | DashboardClientStoreAuthority
  | DashboardSupplierAuthority;

export type DashboardAnalyticsKeyInput = DashboardAuthorityKeyInput & {
  period: DashboardPeriodKind;
  from?: string;
  to?: string;
  history: DashboardHistory;
};

export type DashboardOperationsKeyInput = DashboardAuthorityKeyInput;

export type DashboardRankingContext =
  | {
      kind: Extract<DashboardRankingKind, "clients" | "products" | "stores">;
      sort: Extract<DashboardRankingSort, "value">;
    }
  | {
      kind: Extract<DashboardRankingKind, "suppliers">;
      sort: Extract<DashboardRankingSort, "return_rate" | "financial_impact">;
    }
  | { kind: "suppliers"; sort: "value" }
  | {
      kind: Extract<DashboardRankingKind, "checkers">;
      sort: Extract<DashboardRankingSort, "checked_items">;
    };

export type DashboardRankingKeyInput = (
  | (DashboardAuthorityKeyInput &
      Exclude<DashboardRankingContext, { kind: "suppliers"; sort: "value" }>)
  | (DashboardUnscopedAuthority & { role: "super" } & {
      kind: "suppliers";
      sort: "value";
    })
) & { period: DashboardPeriodState };

export type DashboardRankingFetchInput = DashboardRankingKeyInput & {
  cursor?: string;
};

export type DashboardDetailSort = "recent" | "created";

interface DashboardDetailKeyBase {
  entityId: string;
  cursor?: string;
}

type DashboardLoadDetailContext = DashboardDetailKeyBase & {
  kind: "load";
  period: null;
  sort: "created";
};

type DashboardHistoricalDetailContext = DashboardDetailKeyBase & {
  kind: Exclude<DashboardDetailKind, "load">;
  period: DashboardPeriodState;
  sort: "recent";
};

export type DashboardLoadDetailKeyInput = DashboardAuthorityKeyInput &
  DashboardLoadDetailContext;

export type DashboardHistoricalDetailKeyInput = DashboardAuthorityKeyInput &
  DashboardHistoricalDetailContext;

export type DashboardDetailKeyInput =
  | DashboardLoadDetailKeyInput
  | DashboardHistoricalDetailKeyInput;

type WithoutDetailCursor<Input> = Input extends DashboardDetailKeyInput
  ? Omit<Input, "cursor">
  : never;

export type DashboardDetailOverviewKeyInput =
  WithoutDetailCursor<DashboardDetailKeyInput>;

const normalizeStoreIds = (storeIds: readonly string[]): readonly string[] =>
  [...new Set(storeIds)].sort((left, right) =>
    left < right ? -1 : left > right ? 1 : 0,
  );

type DashboardStoreAuthorityFingerprint = readonly [
  scopeType: DashboardStoreAuthority["scopeType"],
  storeIds: readonly string[],
  selectedStoreId?: string,
];

type DashboardAuthorityFingerprint = readonly [
  role: DashboardRole,
  actorId: string,
  clientId: string | null,
  supplierId: string | null,
  storeAuthority: DashboardStoreAuthorityFingerprint | null,
];

export const dashboardAuthorityFingerprint = (
  input: DashboardAuthorityKeyInput,
): DashboardAuthorityFingerprint => {
  switch (input.role) {
    case "client":
      return [input.role, input.actorId, input.clientId, null, null];
    case "client_store":
      return [
        input.role,
        input.actorId,
        input.clientId,
        null,
        input.selectedStoreId
          ? [
              input.storeScope.scopeType,
              normalizeStoreIds(input.storeScope.storeIds),
              input.selectedStoreId,
            ]
          : [
              input.storeScope.scopeType,
              normalizeStoreIds(input.storeScope.storeIds),
            ],
      ];
    case "supplier":
      return [input.role, input.actorId, null, input.supplierId, null];
    case "checker":
    case "overviewer":
    case "super":
    case "admin_helper":
      return [input.role, input.actorId, null, null, null];
  }
};

const dashboardDetailOverviewKey = (input: DashboardDetailOverviewKeyInput) => {
  const periodKey =
    input.kind === "load" ? null : serializeDashboardPeriod(input.period);
  return [
    "dashboard",
    "v2",
    "detail",
    dashboardAuthorityFingerprint(input),
    input.kind,
    input.entityId,
    periodKey,
    input.sort,
  ] as const;
};

export const dashboardKeys = {
  all: ["dashboard", "v2"] as const,

  analytics(input: DashboardAnalyticsKeyInput) {
    serializeDashboardPeriod({
      period: input.period,
      from: input.from,
      to: input.to,
      history: input.history,
    });

    return [
      ...dashboardKeys.all,
      "analytics",
      dashboardAuthorityFingerprint(input),
      input.period,
      input.from ?? null,
      input.to ?? null,
      input.history,
    ] as const;
  },

  operations(input: DashboardOperationsKeyInput) {
    return [
      ...dashboardKeys.all,
      "operations",
      dashboardAuthorityFingerprint(input),
    ] as const;
  },

  supplierCurrentLoads(input: DashboardSupplierAuthority) {
    return [
      ...dashboardKeys.all,
      "supplier-current-loads",
      dashboardAuthorityFingerprint(input),
    ] as const;
  },

  ranking(input: DashboardRankingKeyInput) {
    return [
      ...dashboardKeys.all,
      "ranking",
      dashboardAuthorityFingerprint(input),
      serializeDashboardPeriod(input.period),
      input.kind,
      input.sort,
    ] as const;
  },

  detailOverview(input: DashboardDetailOverviewKeyInput) {
    return dashboardDetailOverviewKey(input);
  },

  detail(input: DashboardDetailKeyInput) {
    return [
      ...dashboardDetailOverviewKey(input),
      input.cursor ?? null,
    ] as const;
  },
};
